import { useState } from "react";
import { useCreate } from "ra-core";
import {
  Briefcase,
  Ruler,
  DollarSign,
  Activity,
  MessageSquare,
  Save,
  MapPin,
  User,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { JOB_TYPES } from "../constants";
import { JOB_STAGES } from "./jobStages";
import { AddressInput } from "../AddressInput";
import { ContactSearchSelect } from "../contacts/ContactSearchSelect";
import { ContactCreateDialog } from "../contacts/ContactCreateDialog";
import type {
  Job,
  Contact,
} from "../../providers/fakerest/dataGenerator/types";

interface JobCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (job: Job) => void;
  contactId?: number;
  defaultAddress?: string;
}

export const JobCreateDialog = ({
  open,
  onOpenChange,
  onSuccess,
  contactId,
  defaultAddress = "",
}: JobCreateDialogProps) => {
  const [create, { isPending: isCreating }] = useCreate();

  const [selectedContactId, setSelectedContactId] = useState<string>(
    contactId ? String(contactId) : "",
  );
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [address, setAddress] = useState(defaultAddress);
  const [jobType, setJobType] = useState<string>("roof_replacement");
  const [roofSqft, setRoofSqft] = useState<string>("");
  const [estimatedValue, setEstimatedValue] = useState<string>("");
  const [status, setStatus] = useState<string>("new_lead");
  const [notes, setNotes] = useState("");

  const handleContactCreated = (contact: Contact) => {
    setSelectedContactId(String(contact.id));
  };

  // Get the effective contact ID (either from prop or selected)
  const effectiveContactId = contactId || Number(selectedContactId);

  const resetForm = () => {
    if (!contactId) {
      setSelectedContactId("");
    }
    setAddress(defaultAddress);
    setJobType("roof_replacement");
    setRoofSqft("");
    setEstimatedValue("");
    setStatus("new_lead");
    setNotes("");
  };

  const handleSubmit = () => {
    if (!effectiveContactId) {
      return;
    }
    if (!address.trim()) {
      return;
    }

    create(
      "jobs",
      {
        data: {
          contact_id: effectiveContactId,
          address: address.trim(),
          job_type: jobType,
          roof_sqft: Number(roofSqft) || 0,
          estimated_value: Number(estimatedValue) || 0,
          status,
          notes: notes.trim(),
          index: 0,
        },
      },
      {
        onSuccess: (data) => {
          onSuccess(data as Job);
          resetForm();
          onOpenChange(false);
        },
      },
    );
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Create New Job
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Contact - only show if contactId not provided */}
          {!contactId && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Contact <span className="required-asterisk">*</span>
              </Label>
              <ContactSearchSelect
                value={selectedContactId}
                onChange={setSelectedContactId}
                placeholder="Search for a contact..."
                onCreateNew={() => setContactDialogOpen(true)}
              />
            </div>
          )}

          {/* Address */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Address <span className="required-asterisk">*</span>
            </Label>
            <AddressInput
              value={address}
              onChange={setAddress}
              placeholder="123 Main St, City, State 12345"
            />
          </div>

          {/* Job Type */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Job Type
            </Label>
            <Select value={jobType} onValueChange={setJobType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {JOB_TYPES.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    <span className="flex items-center gap-2">
                      <type.icon className={`h-4 w-4 ${type.color}`} />
                      {type.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Roof Sq Ft */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Ruler className="h-4 w-4" />
              Roof Sq Ft
            </Label>
            <Input
              type="number"
              value={roofSqft}
              onChange={(e) => setRoofSqft(e.target.value)}
              placeholder="2500"
            />
          </div>

          {/* Estimated Value */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Estimated Value
            </Label>
            <Input
              type="number"
              value={estimatedValue}
              onChange={(e) => setEstimatedValue(e.target.value)}
              placeholder="15000"
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Status
            </Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {JOB_STAGES.map((stage) => (
                  <SelectItem key={stage.value} value={stage.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${stage.color}`} />
                      {stage.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Notes
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about the job..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isCreating || !effectiveContactId || !address.trim()}
          >
            <Save className="h-4 w-4 mr-2" />
            {isCreating ? "Creating..." : "Create Job"}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Contact Create Dialog */}
      <ContactCreateDialog
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
        onSuccess={handleContactCreated}
      />
    </Dialog>
  );
};
